//* 组件库
import type { UiTypes, UiSelect, UiFormItem } from "various-ui";
import { $loading } from "various-ui";
//* 公共函数
import { Formats } from "@/assets/js";
//* 私有组件
import type ModuleSequence from "@/views/tool/components/sequence/index.vue";
import type ModuleMutationInfo from "../components/mutations-info/index.vue";
//* 私有组件属性
import type { DrawEmitInfoData } from "@/views/tool/components/sequence/src/types";

export const useComposable = (data: Ref<any>) => {
    //* 插件
    const i18n = useI18n();
    const route = useRoute();

    //* 静态变量
    const vars = {
        origin: <any>{},
        modes: [
            { name: i18n.t("GenomicMutations"), value: "" },
            { name: i18n.t("ProteinMutations"), value: "codon" },
        ],

        alt: "",
        //* 颜色对象
        colors: <{ [name: string]: string }>{
            "Pathogenic": "#ce242480",
            "Likely Pathogenic": "#f9ab2480",
            "Likely Benign": "#6a80c880",
            "Benign": "#35a85380",
            "Uncertain": "#99999980",
        },
    };

    //* 响应式变量
    const refs = {
        id: ref<string>(Formats.query(route.query.id) || data.value?.mrnas?.[0]?.value || ""), //* 转录本ID
        all: ref<boolean>(false),
        mode: ref<string>(Formats.query(route.query.mode)), //* 绘图模式
        exon: ref<string>(""),
        range: ref<number[][]>([]),
        exons: ref<UiTypes.candidate[]>([]),
        selects: ref<string[]>([]),
        keyword: ref<string>(Formats.query(route.query.keyword)),
        //* 绘图突变筛选项
        selector: ref<{ name: string; value: string; number: number }[]>([
            { name: i18n.t("Pathogenic", 2), value: "Pathogenic", number: 0 },
            { name: i18n.t("LikelyPathogenic", 2), value: "Likely Pathogenic", number: 0 },
            { name: i18n.t("LikelyBenign", 2), value: "Likely Benign", number: 0 },
            { name: i18n.t("Benign", 2), value: "Benign", number: 0 },
            { name: i18n.t("Uncertain", 2), value: "Uncertain", number: 0 },
        ]),
    };

    //* 节点
    const nodes = {
        modal: ref<InstanceType<typeof ModuleMutationInfo>>(),
        select: ref<InstanceType<typeof UiSelect>>(),
        header: ref<HTMLDivElement>(),
        checker: ref<InstanceType<typeof UiFormItem>>(),
        sequence: ref<InstanceType<typeof ModuleSequence>>(),
    };

    //* 函数列表
    const methods = {
        //* 页面跳转
        next: () => {
            open(
                routerResolve({ name: "BioSequenceResult", query: { id: refs.id.value, mode: "cDNA" }, params: { id: route.params.id } })
                    .href as string,
                "_blank"
            );
        },

        //* 模式切换函数
        mode: (mode?: string) => {
            //* 数据处理
            if (refs.mode.value == mode) return;
            //* 数据初始化
            const config = { compare: <any>{}, result: <number[][]>[], site: 1 };
            if (mode == "codon") {
                vars.origin?.coordinate?.forEach((coordinate: any) => {
                    if (coordinate?.type != "exon" || !coordinate?.start || !coordinate?.end) return;
                    for (let i = coordinate.start; i <= coordinate.end; i++) {
                        //* 检测是否不存在UTR区域
                        const result = coordinate?.utr?.reduce((_result: boolean, value: number[]) => {
                            if (i <= value[1] && i >= value[0]) return false;
                            else {
                                return _result;
                            }
                        }, true);

                        if (result) {
                            config.compare[i] = config.site++;
                        }
                    }
                });

                refs.range.value.forEach((range) => {
                    for (let i = range[0]; i <= range[1]; i++) {
                        if (!config.compare[i]) continue;
                        else {
                            //* 检测是否已存在相同的位点
                            const site = Math.ceil(config.compare[i] / 3);
                            const result = config.result.reduce((_result: boolean, value: number[]) => {
                                if (site <= value[1] && site >= value[0]) return false;
                                else {
                                    return _result;
                                }
                            }, true);

                            if (result) {
                                config.result.push([site, site]);
                            }
                        }
                    }
                });
            } else {
                vars.origin?.coordinate?.forEach((coordinate: any) => {
                    if (coordinate?.type != "exon" || !coordinate?.start || !coordinate?.end) return;
                    for (let i = coordinate.start; i <= coordinate.end; i++) {
                        //* 检测是否不存在UTR区域
                        const result = coordinate?.utr?.reduce((_result: boolean, value: number[]) => {
                            if (i <= value[1] && i >= value[0]) return false;
                            else {
                                return _result;
                            }
                        }, true);

                        if (result) {
                            config.compare[config.site++] = i;
                        }
                    }
                });

                refs.range.value.forEach((range) => {
                    for (let i = range[0]; i <= range[1]; i++) {
                        const site = (i - 1) * 3 + 1;
                        for (let ia = 0; ia < 3; ia++) {
                            const r = config.compare[site + ia];
                            if (!r) continue;
                            else {
                                config.result.push([r, r]);
                            }
                        }
                    }
                });
            }

            //* 数据更新
            vars.alt = "";
            refs.mode.value = mode || "";
            refs.range.value = config.result;
            refs.keyword.value = "";
            //* 状态重置
            nodes.checker.value?.hidden();
            //* 渲染图形
            methods.init(refs.range.value);
        },

        //* 数据获取
        data: (context?: string) => {
            //* 数据初始化
            const keyword = context?.trim();
            //* 数据获取
            $loading.open();
            api.frontend("/api/gene/mrna", {
                data: { id: route.params.id, mrna: refs.id.value },
                method: "POST",
                success: (response) => {
                    //* 数据缓存
                    vars.origin = response;
                    if (!keyword) return methods.init();
                    else {
                        //* 突变位点解析
                        api.frontend("/api/mutations/analyze", {
                            data: { id: route.params.id, keyword: keyword, transcript: refs.id.value },
                            hidden: true,
                            method: "POST",
                            error: () => methods.init(),
                            success: (response) => {
                                //* 数据更新
                                vars.alt = response.alt || "";
                                refs.mode.value = response?.mode || refs.mode.value;
                                if (response?.mode == "codon") {
                                    if (response?.range_codon?.length >= 2) refs.range.value = [response.range_codon];
                                } else if (response?.range?.length >= 2) {
                                    refs.range.value = [response.range];
                                }

                                //* 可视化图形绘制
                                methods.init(refs.range.value);
                            },
                        });
                    }
                },
            });
        },

        //* 初始化
        init: async (radius?: number[][]) => {
            //* 更新加载器
            $loading.open({ message: i18n.t("UnderLoad") });

            //* 数据初始化
            const number: { [name: string]: number } = {};
            const result = {
                size: Math.max(screen.height - (nodes.header.value?.offsetHeight || 0) - 264, 520),
                origin: vars.origin,
                radius: radius,
                selects: <string[]>[],
                message: i18n.t("UnderLoad"),
                mutations: <{ [name: string]: any }>{},
            };

            //* 接口数据获取
            const response = await api.frontend("/api/mutations", {
                data: { id: route.params.id, mrna: refs.id.value, mode: refs.mode.value },
                method: "POST",
            });

            //* 数据处理
            response?.forEach((value: any) => {
                //* 突变数量统计
                if (!number[value.status]) number[value.status] = 1;
                else {
                    number[value.status] += 1;
                }

                //* 突变数据分组
                if (!result.mutations[value.site]) {
                    result.mutations[value.site] = { count: 1, data: {} };
                    result.mutations[value.site].data[value.status] = [value];
                } else if (!result.mutations[value.site].data[value.status]) {
                    result.mutations[value.site].count += 1;
                    result.mutations[value.site].data[value.status] = [value];
                } else {
                    result.mutations[value.site].count += 1;
                    result.mutations[value.site].data[value.status].push(value);
                }
            });

            //* 数据更新
            refs.exon.value = "";
            //* 选项数据更新
            refs.all.value = true;
            refs.selects.value = [];
            refs.selector.value.forEach((value) => {
                value.number = number[value.value] || 0; //* 选项数量更新
                //* 选项状态更新
                if (number[value.value]) {
                    refs.selects.value.push(value.value);
                    result.selects.push(value.value);
                }
            });

            //* 绘图
            nodes.sequence.value?.render(refs.mode.value, result, (data: any) => {
                refs.exons.value = data?.exons || [];
            });

            //* 路由更新
            routerPush({
                name: "BioVariantResult",
                query: { id: refs.id.value, mode: refs.mode.value, keyword: refs.keyword.value },
                params: { id: route.params.id },
                replace: true,
            });
        },

        //* 位点的突变信息
        info: (data: DrawEmitInfoData) => {
            nodes.modal.value?.open(route.params.id.toString(), Object.assign(data, { mrna: refs.id.value }));
        },

        //* 外显子区域检索
        exon: (data?: UiTypes.candidate) => {
            if (data) {
                refs.exon.value = data.label;
                nodes.sequence.value?.update({ site: data.value, area: data.label });
            }
        },

        //* 检索函数
        search: () => {
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
            api.frontend("/api/mutations/site", {
                method: "POST",
                hidden: true,
                data: {
                    id: route.params.id,
                    mode: refs.mode.value,
                    mrna: refs.id.value,
                    keyword: refs.keyword.value.trim(),
                },
                finally: () => $loading.close(),
                error: (error) => {
                    //* 数据更新
                    refs.range.value = [];
                    //* 状态更新
                    nodes.sequence.value?.update({ radius: [] });
                    nodes.checker.value?.show(error.message, "error");
                },
                success: (response) => {
                    //* 数据更新
                    vars.alt = response.alt || "";
                    refs.range.value = response.range;
                    //* 状态更新
                    nodes.sequence.value?.update({ radius: response.range, tips: true });
                    nodes.checker.value?.hidden();
                },
            });
        },

        //* 转录本切换
        change: () => {
            //* 数据更新
            vars.alt = "";
            refs.range.value = [];
            refs.keyword.value = "";
            //* 状态更新
            nodes.checker.value?.hidden();
            //* 页面初始化
            methods.data();
        },

        //* 隐藏下拉框窗口
        hidden: () => nodes.select.value?.hidden(),
        //* 突变类型赛选功能
        checkbox: (status: boolean) => {
            //* 数据修正
            if (!status) refs.all.value = refs.selector.value.filter((value) => value.number).length == refs.selects.value.length;
            else {
                if (!refs.all.value) refs.selects.value.splice(0, refs.selects.value.length);
                else {
                    refs.selector.value.forEach((value) => {
                        if (!refs.selects.value.includes(value.value) && value.number) {
                            refs.selects.value.push(value.value);
                        }
                    });
                }
            }

            //* 序列状态更新
            nodes.sequence.value?.update({ selects: refs.selects.value });
        },

        //* 预测函数
        prediction: (data: { cds: string; carry: boolean; site: string; mode: "RNA-Splicer" | "Pathogenicity-Predictor" }) => {
            //* 接口请求
            $loading.open({ message: "" });
            api.frontend("/api/bio/variant/params", {
                method: "POST",
                data: {
                    id: route.params.id,
                    mode: data.mode,
                    type: refs.mode.value,
                    site: data.site,
                    nucleotide:
                        data.carry && vars.alt
                            ? `${data.cds}${refs.mode.value == "codon" ? Contrast.Codon[vars.alt]?.name : `>${vars.alt}`}`
                            : data.cds,
                    transcript: refs.id.value,
                },
                finally: () => $loading.close(),
                success: (response) => {
                    if (data.mode == "RNA-Splicer") {
                        open(routerResolve({ name: "RnaSplicer", query: { uuid: response } }).href, "_blank");
                    } else {
                        open(routerResolve({ name: "Pathogenicity", query: { uuid: response } }).href, "_blank");
                    }
                },
            });
        },
    };

    //* 计算属性
    const computeds = {
        exons: computed(() => {
            const context = `${refs.exon.value}`.trim().toLocaleLowerCase();
            return refs.exons.value.filter((exon: UiTypes.candidate) => {
                return `${exon.label}`.toLocaleLowerCase().includes(context);
            });
        }),
    };

    return { vars, refs, nodes, methods, computeds };
};
